import type { Schemas } from '@npc/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'

import { api } from './api'
import { call } from './errors'

export type BugReport = Schemas['BugReport']
export type BugReporterConfig = Schemas['BugReporterConfig']

export function useBugReports(filters: { project_id?: string; status?: string; category?: string; risk_level?: string } = {}, enabled=true) {
  return useQuery({
    enabled,
    queryKey: ['bug-reports', filters],
    queryFn: () => call('/bug-reports', () => api.GET('/bug-reports', { params: { query: filters } })),
    refetchInterval: 5000,
  })
}

export function useBugReporterConfig(projectId?: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'bug-reporter'],
    enabled: Boolean(projectId),
    retry: false,
    queryFn: async () => (await call('/projects/{projectId}/bug-reporter', () => api.GET('/projects/{projectId}/bug-reporter', { params: { path: { projectId: projectId! } } }))).config,
  })
}

export function usePutBugReporterConfig() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, ...body }: Schemas['BugReporterConfigPut'] & { projectId: string }) =>
      call('/projects/{projectId}/bug-reporter', () => api.PUT('/projects/{projectId}/bug-reporter', { params: { path: { projectId } }, body })),
    onSuccess: (_data, input) => void client.invalidateQueries({ queryKey: ['projects', input.projectId, 'bug-reporter'] }),
  })
}

export function useDecideBugReport() {
  const client = useQueryClient()
  const request = useRef({body:'',key:''})
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['BugAdminDecision'] & { id: string }) => {
      const fingerprint=JSON.stringify({id,...body})
      if(request.current.body!==fingerprint)request.current={body:fingerprint,key:crypto.randomUUID()}
      return call('/bug-reports/{id}/decision', () => api.POST('/bug-reports/{id}/decision', {
        params: { path: { id }, header: { 'X-Idempotency-Key': request.current.key } }, body,
      }))
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['bug-reports'] }),
  })
}
